import { useState, useEffect } from 'react';
import { X, Trash2, Check } from 'lucide-react';
import useAutomationsStore from '../../../../../store/useAutomationsStore';
import { CATEGORY_COLORS, categoryForNodeType } from '../../constants';

import WebhookConfig from './configs/WebhookConfig';
import ScheduleConfig from './configs/ScheduleConfig';
import WalletTwoEventConfig from './configs/WalletTwoEventConfig';
import SlackConfig from './configs/SlackConfig';
import TelegramConfig from './configs/TelegramConfig';
import SendGridConfig from './configs/SendGridConfig';
import WalletLoadConfig from './configs/WalletLoadConfig';
import TransferConfig from './configs/TransferConfig';
import ConditionConfig from './configs/ConditionConfig';
import DelayConfig from './configs/DelayConfig';
import ForEachConfig from './configs/ForEachConfig';
import GetMemberConfig from './configs/GetMemberConfig';
import GetMembersConfig from './configs/GetMembersConfig';
import ManualTriggerConfig from './configs/ManualTriggerConfig';
import ApiRequestConfig from './configs/ApiRequestConfig';

const CONFIG_COMPONENTS = {
  trigger_webhook: WebhookConfig,
  trigger_schedule: ScheduleConfig,
  trigger_manual: ManualTriggerConfig,
  trigger_onchain: null,
  trigger_wallettwo: WalletTwoEventConfig,
  action_slack: SlackConfig,
  action_telegram: TelegramConfig,
  action_sendgrid: SendGridConfig,
  action_api_request: ApiRequestConfig,
  wallet_load: WalletLoadConfig,
  wallet_transfer: TransferConfig,
  wallet_balance: null,
  wallet_sign: null,
  wallettwo_get_member: GetMemberConfig,
  wallettwo_get_members: GetMembersConfig,
  logic_condition: ConditionConfig,
  logic_delay: DelayConfig,
  logic_foreach: ForEachConfig,
};

export default function NodeConfigDrawer() {
  const { selectedNode, updateNodeData, removeNode, setSelectedNode } = useAutomationsStore();
  const [localData, setLocalData] = useState({});

  useEffect(() => {
    if (selectedNode) {
      setLocalData(selectedNode.data || {});
    }
  }, [selectedNode]);

  if (!selectedNode) return null;

  const nodeType = selectedNode.data?.nodeType;
  const category = categoryForNodeType(nodeType);
  const color = CATEGORY_COLORS[category];
  const ConfigComponent = CONFIG_COMPONENTS[nodeType];

  const handleChange = (key, value) => {
    setLocalData((prev) => ({ ...prev, [key]: value }));
    // Persist test-discovered variables immediately so VariablePicker can see them
    if (key === 'lastTestParameters') {
      updateNodeData(selectedNode.id, { ...localData, [key]: value });
    }
  };

  const handleSave = () => {
    updateNodeData(selectedNode.id, localData);
    setSelectedNode(null);
  };

  const handleDelete = () => {
    removeNode(selectedNode.id);
  };

  return (
    <div className="w-[320px] bg-white border-l border-gray-200 h-full flex flex-col shrink-0 shadow-lg">
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full" style={{ backgroundColor: color }} />
          <h3 className="text-sm font-semibold text-gray-800">{selectedNode.data?.label}</h3>
        </div>
        <button
          onClick={() => setSelectedNode(null)}
          className="p-1 rounded-lg hover:bg-gray-100 transition"
        >
          <X className="w-4 h-4 text-gray-400" />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto p-4">
        {ConfigComponent ? (
          <ConfigComponent data={localData} onChange={handleChange} />
        ) : (
          <p className="text-sm text-gray-400 text-center py-8">
            No configuration needed for this node.
          </p>
        )}
      </div>

      <div className="px-4 py-3 border-t border-gray-200 flex items-center gap-2">
        <button
          onClick={handleDelete}
          className="flex items-center gap-1.5 px-3 py-2 text-sm text-red-500 bg-red-50 hover:bg-red-100 rounded-lg transition"
        >
          <Trash2 className="w-3.5 h-3.5" />
          Delete
        </button>
        <button
          onClick={handleSave}
          className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-sm text-white rounded-lg transition"
          style={{ backgroundColor: color }}
        >
          <Check className="w-3.5 h-3.5" />
          Save Changes
        </button>
      </div>
    </div>
  );
}
